// Canvas-based receipt image compression. Used at every Submit Receipt entry
// point to keep upload payloads small without adding a runtime dependency.
//
// Behavior:
//   - Images (png/jpg/jpeg): downscale to MAX_LONGEST_SIDE if larger, re-encode
//     as JPEG at QUALITY. We always re-encode (not just downscale) because
//     phone cameras emit very large JPEGs even at modest dimensions; JPEG q=0.8
//     on Canvas is essentially indistinguishable for receipt photos.
//   - PDFs and anything else: passthrough unchanged. PDF re-encoding is out of
//     scope and would need a separate dep.
//
// Failure mode: if Canvas/Image/URL aren't available (very old browser) or the
// image fails to decode, we return the original file untouched so the upload
// path still works. Callers always get a File back.

const MAX_LONGEST_SIDE = 2048
const QUALITY = 0.8
const COMPRESSIBLE_TYPES = ['image/jpeg', 'image/jpg', 'image/png']
const COMPRESSIBLE_EXTENSIONS = ['jpg', 'jpeg', 'png']

function getExtension (name) {
  if (!name) return ''
  const parts = name.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
}

function shouldCompress (file) {
  if (!file) return false
  if (file.type && COMPRESSIBLE_TYPES.indexOf(file.type.toLowerCase()) !== -1) {
    return true
  }
  // Some mobile pickers leave file.type empty — fall back to extension.
  return COMPRESSIBLE_EXTENSIONS.indexOf(getExtension(file.name)) !== -1
}

function computeTargetDimensions (width, height) {
  const longest = Math.max(width, height)
  if (longest <= MAX_LONGEST_SIDE) {
    return { width: width, height: height }
  }
  const scale = MAX_LONGEST_SIDE / longest
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale)
  }
}

function renameToJpeg (name) {
  if (!name) return 'receipt.jpg'
  const lastDot = name.lastIndexOf('.')
  if (lastDot === -1) return name + '.jpg'
  return name.substring(0, lastDot) + '.jpg'
}

// Compress an image File and return a new File. PDFs and unknowns pass through.
// Resolves with the original file on any failure — never throws to the caller.
export function compressReceiptImage (file) {
  return new Promise(function (resolve) {
    if (!shouldCompress(file)) {
      resolve(file)
      return
    }
    if (typeof window === 'undefined' || !window.URL || !window.URL.createObjectURL) {
      resolve(file)
      return
    }

    const objectUrl = window.URL.createObjectURL(file)
    const img = new window.Image()

    const cleanup = function () {
      try { window.URL.revokeObjectURL(objectUrl) } catch (e) {}
    }

    img.onload = function () {
      const target = computeTargetDimensions(img.naturalWidth || img.width, img.naturalHeight || img.height)
      const canvas = document.createElement('canvas')
      canvas.width = target.width
      canvas.height = target.height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        cleanup()
        resolve(file)
        return
      }
      ctx.drawImage(img, 0, 0, target.width, target.height)
      canvas.toBlob(function (blob) {
        cleanup()
        if (!blob) {
          resolve(file)
          return
        }
        // Never return a larger artifact than we started with — for already-tiny
        // images JPEG re-encoding can slightly inflate the bytes.
        if (blob.size >= file.size) {
          resolve(file)
          return
        }
        try {
          const compressed = new File([blob], renameToJpeg(file.name), {
            type: 'image/jpeg',
            lastModified: Date.now()
          })
          resolve(compressed)
        } catch (err) {
          // Edge / older browsers without File constructor — return the blob
          // wrapped to look enough like a File for FormData purposes.
          blob.name = renameToJpeg(file.name)
          blob.lastModified = Date.now()
          resolve(blob)
        }
      }, 'image/jpeg', QUALITY)
    }

    img.onerror = function () {
      cleanup()
      resolve(file)
    }

    img.src = objectUrl
  })
}

export default compressReceiptImage



// WEBPACK FOOTER //
// ./src/core/receiptScan/imageCompression.js